import * as React from 'react'
import {useEffect, useMemo} from 'react'
import {useObserver} from "mobx-react";
import accessStore from "./AccessStore";
import SelectedLocationsStore from "./SelectedLocationsStore";
import {formatPostalAddress} from "./locationAddress/util";
import reactSelectStyles from "../config/reactSelect/reactSelectStyles";
import reactSelectTheme from '../config/reactSelect/reactSelectTheme';
import {cssVar} from '../../components/cssInJs';
import {
  DropdownIndicator,
  IndicatorsContainer,
  IndicatorSeparator,
  LoadingIndicator,
  MenuList
} from '../config/reactSelect/components';
import AsyncPaginate from "react-select-async-paginate";
import { initialPagination, loadOptions } from "./locationGroupPaginatedSearch"
import { useState } from 'react';
import variables from "../../../src/components/bootstrap4/includes/_core-variables.scss"

export function ReactSelectLocationSelector({
  shouldNotIncludeAccounts = false
}) {

  const initialAdditional = useMemo(() => {
    return initialPagination({includeAccounts: !shouldNotIncludeAccounts});
  }, [shouldNotIncludeAccounts]);

  const selected = useObserver(() => SelectedLocationsStore.selectedEntity);
  useEffect(() => {
    SelectedLocationsStore.resolveSelection();
  }, []);

  function handleResize() {
    if(!hasResized) {
      setHasResized(true);
    }
    setContainerX(`${document.querySelector(".location-search-box").getBoundingClientRect().left}px`);
  }

  const [hasResized, setHasResized] = useState(false);
  const [containerX, setContainerX] = useState(undefined);

  // Let react-select handle the first offset query to avoid querying too early and getting an incorrect reading on page load.
  const leftValue = document.querySelector("#contentArea") === null && !hasResized ? {} : window.innerWidth <= 768 && document.querySelector("#contentArea") !== null ? {left: `${variables.cardPaddingX}`} : {left: containerX} 

  useEffect(() => {
    // Update menu portal left position on resize.
    if(window.ResizeObserver && document.querySelector("#contentArea") !== null) { // If element is undefined in the DOM, it needs 
      const resizeObserver = new ResizeObserver(() => {
        setContainerX(`${document.querySelector(".location-search-box").getBoundingClientRect().left}px`);
      });
      resizeObserver.observe(document.querySelector("#contentArea"));
      return () => resizeObserver.unobserve(document.querySelector("#contentArea"));
    } else {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  
  return <AsyncPaginate
    menuPortalTarget={document.getElementById("PAGE_FILTERS")}
    menuPosition={'fixed'}
    loadOptions={loadOptionsReactSelect}
    styles={reactSelectStyles({
      container: s => ({...s}),
      menu: s => ({...s, zIndex: 2000, border: "var(--card-border)", boxShadow: "var(--box-shadow)", marginTop: 0}),
      menuPortal: s => ({...s, border: "none", ...leftValue})    
    })}
    additional={initialAdditional}
    debounceTimeout={300}
    components={{DropdownIndicator, IndicatorsContainer, LoadingIndicator, IndicatorSeparator, MenuList}}
    theme={reactSelectTheme}
    menuShouldScrollIntoView={false}
    shouldLoadMore={(scrollHeight, clientHeight, scrollTop) => {
      return scrollHeight <= clientHeight || (scrollHeight - scrollTop) <= 500; // start loading within 500 px
    }}
    defaultOptions
    onChange={x => SelectedLocationsStore.setSelectedLocationGroup(x)}
    getOptionValue={o => o.entityIdString}
    placeholder={"Locations..."}
    value={selected}
    formatGroupLabel={x => <strong style={{textTransform: 'capitalize', color: cssVar("--input-color"), fontSize: '1rem'}}>{x.label}</strong> }
    getOptionLabel={
      /** @param {LocationGroup} option */
        option => {
        const locationCountText = option.entityId.entityType === 'Location' ?
          '' :
          `(${option.totalLocations})`;
        const address = option.address && formatPostalAddress(option.address)
        const label = option.name + (address ? ', ' + address : '')
        return (
          <span>{label} {locationCountText}</span>)
      }}
  />
}


const typeToHeader = {
  "Location": "Locations",
  "Group": "Groups",
  "Account": "Accounts",
  "User": "All Locations"
}

/**
 *
 * @param {string} search
 * @param {any} current
 * @param {LocationGroupPagination} additional
 * @returns {Promise<{options: LocationGroup[], hasMore: boolean, additional: LocationGroupPagination}>}
 */
async function loadOptionsReactSelect(search, current, additional) {
  try {
    const result = await loadOptions(search, additional)
    return {
      ...result,
      ...(addHeaders(result.options, result.additional))
    }
  } catch (ex) {
    console.error(ex)
  }
}

/**
 * modifies result type by adding in section headers when a new type is encountered (and tracks added headers in `additional`
 * @param {LocationGroup[]} options
 * @param {LocationGroupPagination & { seenTypes?: string[]}} additional
 * @returns {{
 *  additional: LocationGroupPagination & { seenTypes?: string[] },
 *  options: ({label: string, options: LocationGroup[]}|LocationGroup)[]
 * }}
 */
function addHeaders(options, additional) {
  const currentSeen = (additional.seenTypes || []).slice();
  return {
    options: options.map(x => {
      let type = x.entityId.entityType;
      if (currentSeen.includes(type)) {
        return x
      } else {
        currentSeen.push(type)
        return { label: typeToHeader[type], options: [x] }
      }
    }),
    additional: { ...additional, seenTypes: currentSeen }
  }

}
